/*
 * Copyright (c) 2025 Autire Technologies, LLC. All Rights Reserved.
 *
 * This product and related documentation is protected by copyright and
 * distributed under licenses restricting its use, copying, distribution and
 * decompilation. No part of this product or related documentation may be
 * reproduced in any form by any means without prior written authorization
 * from Autire Technologies, LLC and/or its licensors.
 */

/**
 * @fileoverview Redux store configuration and middleware setup
 * @description Configures the Redux store with middleware including thunk for async actions
 * and logger for development debugging. The store is configured differently for development
 * and production environments to optimize performance and debugging capabilities.
 *
 * @module ReduxStore
 * @author PKP Technologies, LLC
 * @since 1.0.0
 * @version 1.0.0
 *
 * @example
 * import { store } from './store';
 *
 * // Access store state
 * const state = store.getState();
 *
 * // Dispatch actions
 * store.dispatch(actionCreator());
 *
 * @see {@link reducers/index.js} For root reducer configuration
 * @see {@link actions/} For action creators
 */

import { createStore, applyMiddleware, compose } from 'redux';
import { thunk } from 'redux-thunk';
import { createLogger } from 'redux-logger';
import rootReducer from '../reducers';

/**
 * Redux logger middleware for development debugging
 * @description Provides detailed logging of Redux actions and state changes
 * in development environment. Automatically disabled in production for performance.
 *
 * @type {Object}
 * @since 1.0.0
 */
const logger = createLogger();

/**
 * Redux middleware array
 * @description Array of middleware functions to be applied to the store.
 * Includes thunk for async actions and logger for development debugging.
 *
 * @type {Array<Function>}
 * @since 1.0.0
 */
let middleware = [];

/**
 * Configure middleware based on environment
 * @description Sets up middleware differently for development and production.
 * Development includes logger for debugging, production only includes thunk.
 *
 * @example
 * // Development environment
 * middleware = [thunk, logger];
 *
 * // Production environment
 * middleware = [thunk];
 *
 * @since 1.0.0
 */
middleware =
  process.env.NODE_ENV === 'development' ? [...middleware, thunk, logger] : [...middleware, thunk];

/**
 * Redux store instance
 * @description The main Redux store configured with middleware and root reducer.
 * Provides state management for the entire application with support for
 * async actions and development debugging.
 *
 * @type {Object}
 * @example
 * // Get current state
 * const currentState = store.getState();
 *
 * // Subscribe to state changes
 * const unsubscribe = store.subscribe(() => {
 *   console.log('State changed:', store.getState());
 * });
 *
 * // Dispatch actions
 * store.dispatch({ type: 'ACTION_TYPE', payload: data });
 *
 * @since 1.0.0
 * @author PKP Technologies, LLC
 * @see {@link rootReducer} For state structure
 * @see {@link thunk} For async action support
 * @see {@link logger} For development debugging
 */
export const store = createStore(rootReducer, compose(applyMiddleware(...middleware)));
